'use client';
import { useEffect, useState } from 'react';

import { getAppInfo } from '@/app/components/MyRoom/appCompat';

// 앱 업데이트 게이트.
//
// 이 앱은 WebView 셸이라 화면 전체를 웹이 그린다. 그래서 여기서 전면 안내를 띄우면
// 사실상 업데이트를 강제할 수 있다 — 네이티브를 고치지 않고도. 앱이 하는 일은
// User-Agent 에 자기 버전을 실어 보내는 것뿐이고(JOYSAM-APP/1.0.0(43)), 기준값은
// 서버 설정(config/app_release.php)에 있어 그날 바로 바꿀 수 있다.
//
// 왜 이렇게까지 하나: 네이티브 수정은 스토어 업데이트를 받은 사람에게만 간다. 2026-09-18
// 에 아이폰 사용자들이 깨진 화면을 봤을 때, 앱을 고쳐도 이미 깔린 앱에는 닿지 않아
// 웹과 서버로 우회해야 했다. 그때 이 게이트가 있었다면 업데이트를 요구할 수 있었다.

const BASE_URL = process.env.BASE_URL;

interface Gate {
    /** 못 넘어간다. 전면 안내를 띄우고 화면을 막는다. */
    blocked: boolean;
    /** 넘어갈 수 있지만 권하는 상태. 닫을 수 있는 안내. */
    suggested: boolean;
    /** 스토어 주소. 아직 등록 못 한 플랫폼은 빈 문자열이다. */
    storeUrl: string;
}

const NO_GATE: Gate = { blocked: false, suggested: false, storeUrl: '' };

export function useAppReleaseGate(): Gate {
    const [gate, setGate] = useState<Gate>(NO_GATE);

    useEffect(() => {
        const { inApp, isIOS, build } = getAppInfo();
        // 모바일 웹은 업데이트할 것이 없다.
        if (!inApp) return;

        let alive = true;
        (async () => {
            try {
                const res = await fetch(`${BASE_URL}/app/release`, { cache: 'no-store' });
                if (!res.ok) return;
                const body = await res.json();
                const rule = body?.data?.[isIOS ? 'ios' : 'android'];
                if (!rule || !alive) return;

                const min = Number(rule.min) || 0;
                const latest = Number(rule.latest) || 0;

                setGate({
                    // min 이 0 이면 아무도 막지 않는다 — 게이트를 넣기만 하고 아직 쓰지 않는 상태.
                    blocked: min > 0 && build < min,
                    suggested: latest > 0 && build < latest && !(min > 0 && build < min),
                    storeUrl: String(rule.store_url || ''),
                });
            } catch {
                // 조회에 실패하면 막지 않는다. 서버가 잠깐 흔들렸다고 전원이 앱을 못 쓰게
                // 되는 쪽이 훨씬 나쁘다.
            }
        })();

        return () => {
            alive = false;
        };
    }, []);

    return gate;
}
